import React, { useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, X } from "lucide-react";

const SUPPORTED_LANGS = ["tr", "en", "es", "de", "fr", "ar"];

// Static texts — show instantly (no typewriter delay)
const LINES = {
  tr: [
    "AKILLI EŞLEŞTİRME",
    "Markana uygun paketi 30 saniyede bul",
    "3 hızlı soru. Sektörüne, bütçene ve önceliklerine göre önerimizi göster.",
  ],
  en: [
    "SMART MATCHING",
    "Find your plan in 30 seconds",
    "3 quick questions. Get our recommendation based on your industry, budget, and goals.",
  ],
  es: ["BÚSQUEDA INTELIGENTE", "Tu plan en 30 segundos", "3 preguntas rápidas para tu recomendación."],
  de: ["SMART MATCHING", "Dein Paket in 30 Sekunden", "3 schnelle Fragen für deine Empfehlung."],
  fr: ["MATCHING INTELLIGENT", "Ton forfait en 30 secondes", "3 questions rapides pour ta recommandation."],
  ar: ["مطابقة ذكية", "اعثر على باقتك في ٣٠ ثانية", "٣ أسئلة سريعة للحصول على توصيتك."],
};

const COPY = {
  tr: { short: "Paketini Bul", cta: "Hemen Başla" },
  en: { short: "Find Plan", cta: "Start Now" },
  es: { short: "Tu Plan", cta: "Empezar" },
  de: { short: "Dein Paket", cta: "Los geht's" },
  fr: { short: "Ton Plan", cta: "Commencer" },
  ar: { short: "ابحث", cta: "ابدأ الآن" },
};

export default function StickyVerticalCTA() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const seg = pathname.split("/").filter(Boolean)[0];
  const lang = SUPPORTED_LANGS.includes(seg) ? seg : "tr";
  const copy = COPY[lang] || COPY.tr;
  const lines = LINES[lang] || LINES.tr;

  const scrollToCalculator = useCallback(() => {
    window.open(`/${lang}/paketini-bul`, "_blank", "noopener,noreferrer");
  }, [lang]);

  if (pathname.startsWith("/admin") || pathname.startsWith("/login")) return null;

  const handleClick = (e) => {
    e.preventDefault();
    setMobileOpen(false);
    scrollToCalculator();
  };

  return (
    <>
      {/* ─── DESKTOP: sliding card from right edge ─── */}
      <motion.div
        className="hidden lg:flex group fixed top-1/2 -translate-y-1/2 z-40 pointer-events-auto items-stretch"
        style={{ right: 0 }}
        animate={{ x: hovered ? -16 : 192 }}
        transition={{ type: "spring", stiffness: 220, damping: 26 }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        data-testid="sticky-vertical-cta"
      >
        {/* Pulse ring on the always-visible edge */}
        <span
          aria-hidden="true"
          className="absolute -start-2 top-1/2 -translate-y-1/2 h-16 w-16 rounded-full bg-brand-pink/45 blur-2xl animate-ping-slow"
        />
        {/* Always-visible vertical handle */}
        <span
          className="relative flex flex-col items-center justify-center gap-3 w-16
                     bg-gradient-to-b from-brand-pink to-brand-magenta text-cream
                     rounded-s-2xl shadow-[0_22px_44px_-14px_rgba(238,79,122,0.85)]
                     border border-cream/30 border-e-0
                     animate-wobble-slow cursor-pointer"
          style={{ animationPlayState: hovered ? "paused" : "running" }}
        >
          <Sparkles className="h-5 w-5" />
          <span
            className="text-[0.65rem] uppercase tracking-[0.28em] font-bold leading-none"
            style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
          >
            {copy.short}
          </span>
        </span>

        {/* Card body — revealed on hover with typewriter */}
        <span
          className="relative flex w-[220px] flex-col items-start justify-center text-start
                     bg-gradient-to-br from-brand-pink to-brand-magenta text-cream
                     rounded-e-2xl rounded-s-none border border-cream/30 border-s-0
                     shadow-[0_22px_44px_-14px_rgba(238,79,122,0.85)]
                     px-5 py-5 cursor-pointer"
          onClick={handleClick}
        >
          {/* Static text — instant, no animation */}
          <span className="text-[0.58rem] uppercase tracking-[0.22em] font-bold opacity-90">
            {lines[0]}
          </span>
          <span className="mt-2 font-display text-sm font-bold leading-snug">
            {lines[1]}
          </span>
          <span className="mt-2 text-[0.68rem] text-cream/80 leading-relaxed line-clamp-3">
            {lines[2]}
          </span>
          <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-cream text-brand-pink-deep px-3 py-1.5 text-xs font-bold">
            {copy.cta} <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </span>
      </motion.div>

      {/* ─── MOBILE: slide-in panel from right, same concept as desktop ─── */}
      {/* Trigger pill */}
      <AnimatePresence>
        {!mobileOpen && (
          <motion.button
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 60 }}
            transition={{ type: "spring", stiffness: 200, damping: 24, delay: 1 }}
            type="button"
            onClick={() => setMobileOpen(true)}
            data-testid="sticky-vertical-cta-mobile"
            className="lg:hidden fixed right-0 top-1/2 -translate-y-1/2 z-40
                       flex flex-col items-center justify-center gap-2 w-12
                       bg-gradient-to-b from-brand-pink to-brand-magenta text-cream
                       rounded-l-2xl py-5
                       shadow-[0_16px_36px_-10px_rgba(238,79,122,0.8)]
                       border border-cream/30 border-r-0"
            aria-label={copy.short}
          >
            <Sparkles className="h-4 w-4" />
            <span
              className="text-[0.6rem] uppercase tracking-[0.2em] font-bold leading-none"
              style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
            >
              {copy.short}
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Mobile slide-in panel */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
            />
            {/* Card */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 220, damping: 26 }}
              className="lg:hidden fixed right-0 top-1/2 -translate-y-1/2 z-50 flex items-stretch"
            >
              {/* Handle strip */}
              <div
                className="flex flex-col items-center justify-center gap-3 w-12
                           bg-gradient-to-b from-brand-pink to-brand-magenta text-cream
                           rounded-l-2xl py-6
                           shadow-[0_22px_44px_-14px_rgba(238,79,122,0.85)]
                           border border-cream/30 border-r-0"
                onClick={() => setMobileOpen(false)}
              >
                <Sparkles className="h-4 w-4" />
                <span
                  className="text-[0.6rem] uppercase tracking-[0.2em] font-bold leading-none"
                  style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
                >
                  {copy.short}
                </span>
              </div>
              {/* Content card */}
              <div
                className="relative flex w-[240px] flex-col items-start justify-center text-start
                           bg-gradient-to-br from-brand-pink to-brand-magenta text-cream
                           rounded-r-2xl border border-cream/30 border-l-0
                           shadow-[0_22px_44px_-14px_rgba(238,79,122,0.85)]
                           px-5 py-6"
              >
                {/* Close btn */}
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="absolute top-3 right-3 h-6 w-6 rounded-full bg-cream/20 flex items-center justify-center"
                >
                  <X className="h-3.5 w-3.5 text-cream" />
                </button>

                {/* Static text — instant */}
                <span className="text-[0.58rem] uppercase tracking-[0.22em] font-bold opacity-90">
                  {lines[0]}
                </span>
                <span className="mt-2 font-display text-sm font-bold leading-snug">
                  {lines[1]}
                </span>
                <span className="mt-2 text-[0.68rem] text-cream/80 leading-relaxed">
                  {lines[2]}
                </span>
                {/* CTA */}
                <button
                  type="button"
                  onClick={handleClick}
                  className="mt-4 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-cream text-brand-pink-deep px-4 py-2.5 text-sm font-bold hover:scale-[1.02] transition-transform"
                >
                  {copy.cta} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
